import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { getApiErrorMessage } from "./helpers/apiErrorMessage";
import AlbumSearchSkeleton from "./AlbumSearchSkeleton";

function Collection() {
    const [query, setQuery] = useState('');
    const [album, setAlbum] = useState(null);
    const [searchResults, setSearchResults] = useState([]);
    const [searchCache, setSearchCache] = useState({});
    const [currentResultIndex, setCurrentResultIndex] = useState(0);
    const [searching, setSearching] = useState(false);
    const [searchStage, setSearchStage] = useState('');
    const [searchProgress, setSearchProgress] = useState({});
    const [searchError, setSearchError] = useState(false);
    const [mediaType, setMediaType] = useState('CD');
    const [collection, setCollection] = useState([]);
    const [expandedDetails, setExpandedDetails] = useState(null);
    const [editing, setEditing] = useState(null);
    const [message, setMessage] = useState('');
    const [isError, setIsError] = useState(false);
    const [savingEntryId, setSavingEntryId] = useState(null);
    const [diarySettings, setDiarySettings] = useState({ enableRatings: true, showCoverArt: true });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const searchSessionRef = useRef(0);

    useEffect(() => {
        fetchCollection();
        fetchDiarySettings();
    }, []);

    const searchAlbum = async () => {
        if (!query.trim() || searching) return;
        const sessionId = searchSessionRef.current + 1;
        searchSessionRef.current = sessionId;

        setAlbum(null);
        setSearching(true);
        setSearchStage('Finding best matches...');
        setSearchError(false);
        try {
            const response = await axios.get(`/api/search?query=${encodeURIComponent(query)}&mode=basic`);
            const results = Array.isArray(response.data)
                ? response.data
                : response.data?.results || (response.data ? [response.data] : []);

            if (!results.length) {
                setAlbum(null);
                setSearchResults([]);
                setCurrentResultIndex(0);
                setSearchError(true);
                return;
            }

            setSearchResults(results);
            const initialCache = {};
            const initialProgress = {};
            for (const result of results) {
                initialCache[result.mbid] = result;
                initialProgress[result.mbid] = {
                    isLoading: true,
                    label: 'Queued for staged loading...',
                    contextLoaded: false,
                    releaseLoaded: false,
                    artistLoaded: false,
                    coverLoaded: false,
                    hasError: false
                };
            }
            setSearchCache(initialCache);
            setSearchProgress(initialProgress);
            setCurrentResultIndex(0);
            setAlbum(results[0]);

            for (const result of results) {
                preloadAlbumStages(result, initialCache, sessionId);
            }
        } catch (error) {
            console.error("Album not found");
            setSearchError(true);
            setSearchStage('Search failed');
        } finally {
            setSearching(false);
        }
    };

    const mergeAlbumData = (mbid, fallbackAlbum, patch) => {
        setSearchCache(prevCache => {
            const merged = { ...(prevCache[mbid] || fallbackAlbum || {}), ...patch };
            return { ...prevCache, [mbid]: merged };
        });

        setSearchResults(prevResults => prevResults.map(result =>
            result.mbid === mbid ? { ...result, ...patch } : result
        ));

        setAlbum(prevAlbum => prevAlbum?.mbid === mbid ? { ...prevAlbum, ...patch } : prevAlbum);
    };

    const updateProgress = (mbid, patch) => {
        setSearchProgress(prevProgress => ({
            ...prevProgress,
            [mbid]: {
                ...(prevProgress[mbid] || {}),
                ...patch
            }
        }));
    };

    const preloadAlbumStages = async (basicAlbum, initialCache, sessionId) => {
        const mbid = basicAlbum?.mbid;
        if (!mbid) return;

        updateProgress(mbid, { label: 'Loading release-group context...', isLoading: true });

        let releaseId = null;
        let artistId = null;

        try {
            const contextResponse = await axios.get(`/api/search/stage/context/${encodeURIComponent(mbid)}`);
            if (searchSessionRef.current !== sessionId) return;

            releaseId = contextResponse.data?.releaseId;
            artistId = contextResponse.data?.artistId;
            const tags = contextResponse.data?.tags || [];

            mergeAlbumData(mbid, initialCache[mbid], { tags });
            updateProgress(mbid, { contextLoaded: true, label: 'Loading release data...' });
        } catch (error) {
            if (searchSessionRef.current !== sessionId) return;
            updateProgress(mbid, {
                hasError: true,
                isLoading: false,
                label: 'Could not load release-group context'
            });
            return;
        }

        if (releaseId) {
            try {
                const releaseResponse = await axios.get(`/api/search/stage/release/${encodeURIComponent(releaseId)}`);
                if (searchSessionRef.current !== sessionId) return;

                const country = releaseResponse.data?.country || null;
                const duration = releaseResponse.data?.duration || 0;
                mergeAlbumData(mbid, initialCache[mbid], { country, duration });
                updateProgress(mbid, { releaseLoaded: true, label: 'Loading artist data...' });
            } catch (error) {
                if (searchSessionRef.current !== sessionId) return;
                updateProgress(mbid, {
                    hasError: true,
                    isLoading: false,
                    label: 'Could not load release data'
                });
                return;
            }
        }

        if (artistId) {
            try {
                const artistResponse = await axios.get(`/api/search/stage/artist/${encodeURIComponent(artistId)}`);
                if (searchSessionRef.current !== sessionId) return;

                const artistCountry = artistResponse.data?.country;
                if (artistCountry) {
                    mergeAlbumData(mbid, initialCache[mbid], { country: artistCountry });
                }
                updateProgress(mbid, { artistLoaded: true, label: 'Loading cover art...' });
            } catch (error) {
                if (searchSessionRef.current !== sessionId) return;
                updateProgress(mbid, {
                    hasError: true,
                    isLoading: false,
                    label: 'Could not load artist data'
                });
                return;
            }
        }

        try {
            const coverResponse = await axios.get(`/api/search/stage/cover/${encodeURIComponent(mbid)}`);
            if (searchSessionRef.current !== sessionId) return;

            mergeAlbumData(mbid, initialCache[mbid], {
                albumCover: coverResponse.data?.albumCover || null,
                detailsLoaded: true
            });
            updateProgress(mbid, {
                coverLoaded: true,
                isLoading: false,
                label: 'Album found'
            });
        } catch (error) {
            if (searchSessionRef.current !== sessionId) return;
            updateProgress(mbid, {
                hasError: true,
                isLoading: false,
                label: 'Could not load cover art'
            });
        }
    };

    const addToCollection = async () => {
        try {
            setIsError(null);
            cancelSearch();
            await axios.post('/api/collection/', {
                ...album,
                media_type: mediaType
            });
            fetchCollection()
        } catch (error) {
            setIsError(true);
            setMessage(getApiErrorMessage(error, 'add this album to collection'));
            console.error("Error adding album to collection");
        }
    };

    const cancelSearch = async () => {
        searchSessionRef.current += 1;
        setAlbum(null);
        setSearchResults([]);
        setSearchCache({});
        setSearchProgress({});
        setCurrentResultIndex(0);
        setSearchStage('');
    };

    const goToPreviousSearchResult = () => {
        if (currentResultIndex <= 0) return;
        const nextIndex = currentResultIndex - 1;
        setCurrentResultIndex(nextIndex);
        const nextAlbum = searchResults[nextIndex];
        setAlbum(searchCache[nextAlbum.mbid] || nextAlbum);
    };

    const goToNextSearchResult = () => {
        if (currentResultIndex >= searchResults.length - 1) return;
        const nextIndex = currentResultIndex + 1;
        setCurrentResultIndex(nextIndex);
        const nextAlbum = searchResults[nextIndex];
        setAlbum(searchCache[nextAlbum.mbid] || nextAlbum);
    };

    const fetchCollection = async () => {
        try {
            setIsError(false);
            const response = await axios.get('/api/collection/');
            setCollection(response.data);
        } catch (error) {
            setIsError(true);
            setMessage(getApiErrorMessage(error, 'load collection entries'));
            console.error('Error fetching collection:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchDiarySettings = async () => {
        try {
            const response = await axios.get('/api/settings/diary');
            setDiarySettings(response.data);
        } catch (error) {
            setIsError(true);
            setMessage(getApiErrorMessage(error, 'load diary settings'));
            console.error('Error fetching diary settings:', error);
        }
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            searchAlbum();
        }
    };

    const toggleDetails = (entryId) => {
        setExpandedDetails(expandedDetails === entryId ? null : entryId);
        setEditing(null);
    };

    const toggleEditing = (entry) => {
        const entryId = entry.collectionEntryId;
        const isClosing = editing === entryId;
        setEditing(isClosing ? null : entryId);
        setExpandedDetails(null);
    };

    const handleMediaTypeChange = (entryId, value) => {
        setCollection(currentCollection => currentCollection.map(entry =>
            entry.collectionEntryId === entryId
                ? { ...entry, mediaType: value }
                : entry
        ));
    };

    const handleSaveChanges = async (entryId) => {
        const entry = collection.find(item => item.collectionEntryId === entryId);
        if (!entry) return;

        try {
            setIsError(false);
            setSavingEntryId(entryId);
            await axios.post('/api/collection/change', {
                collectionEntryId: entryId,
                mediaType: entry.mediaType
            });
        } catch (error) {
            setIsError(true);
            setMessage(getApiErrorMessage(error, 'update this collection entry'));
            console.error("Error changing collection:", error);
            fetchCollection();
        } finally {
            setSavingEntryId(null);
        }
    };

    const handleDelete = async (entryId) => {
        const previousCollection = collection;
        setCollection(currentCollection => currentCollection.filter(entry => entry.collectionEntryId !== entryId));

        try {
            setIsError(false);
            await axios.delete(`/api/collection/${entryId}`);
        } catch (error) {
            setIsError(true);
            setMessage(getApiErrorMessage(error, 'delete this collection entry'));
            console.error("Error deleting album: ", error);
            setCollection(previousCollection);
        }
    };

    const currentSearchResult = searchResults[currentResultIndex] || null;
    const currentMbid = currentSearchResult?.mbid;
    const currentAlbum = currentMbid ? (searchCache[currentMbid] || currentSearchResult) : album;
    const currentProgress = currentMbid ? searchProgress[currentMbid] : null;
    const showSkeleton = searching || Boolean(currentProgress?.isLoading);

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-950 via-gray-900 to-zinc-900 text-white flex items-center justify-center">
                <div className="rounded-2xl border border-white/10 bg-white/5 px-8 py-6 shadow-2xl backdrop-blur">
                    <p className="text-lg tracking-wide text-gray-200">Loading your collection...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="relative min-h-screen overflow-hidden bg-gradient-to-b from-slate-950 via-gray-900 to-zinc-950 text-white">
            <div className="pointer-events-none absolute -left-24 top-20 h-72 w-72 rounded-full bg-emerald-500/20 blur-3xl" />
            <div className="pointer-events-none absolute -right-20 top-72 h-80 w-80 rounded-full bg-cyan-500/10 blur-3xl" />
            <div className="pointer-events-none absolute bottom-10 left-1/3 h-64 w-64 rounded-full bg-orange-500/10 blur-3xl" />

            <div className="relative z-10 mx-auto flex w-full max-w-6xl flex-col gap-8 px-4 pb-10 pt-6 sm:px-6 lg:px-8">
            {error && (
                <div className="rounded-xl border-l-4 border-red-500 bg-red-950/70 p-4 shadow-lg backdrop-blur-sm">
                    <div className="flex justify-between items-center">
                        <p className="text-red-200">{error}</p>
                        <button
                            onClick={() => setError(null)}
                            className="text-2xl text-red-400 hover:text-red-200"
                        >
                            x
                        </button>
                    </div>
                </div>
            )}

            <section className="rounded-3xl border border-white/10 bg-gradient-to-r from-gray-900/85 via-slate-900/80 to-gray-900/85 px-5 py-6 shadow-2xl backdrop-blur-md sm:px-8">
                <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                        <p className="text-xs uppercase tracking-[0.25em] text-emerald-300/80">Collection Explorer</p>
                        <h1 className="text-2xl font-black leading-tight text-white sm:text-3xl">Find Albums, Then Lock Them In</h1>
                    </div>
                    <p className="max-w-md text-sm text-gray-300">Search MusicBrainz by title, artist, or MBID and add entries to your collection in one flow.</p>
                </div>

                <div className="flex w-full flex-col items-stretch gap-2 sm:flex-row sm:items-center">
                    <input
                        type="text"
                        placeholder="Search for an album or use 'mbid:'"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={handleKeyDown}
                        className="w-full flex-grow rounded-xl border border-white/10 bg-gray-800/90 px-4 py-3 text-gray-200 shadow-inner focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                    <button
                        className="rounded-xl bg-emerald-500 px-5 py-3 font-semibold text-white transition-all duration-200 hover:-translate-y-0.5 hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-70"
                        onClick={searchAlbum}
                        disabled={searching}
                    >
                        {searching ? (
                            <span className="flex items-center justify-center gap-2">
                                <i className="fas fa-spinner animate-spin"></i>
                                Searching
                            </span>
                        ) : 'Search'}
                    </button>
                </div>

                {(currentAlbum || showSkeleton) && (
                    <div className="mt-6 rounded-2xl border border-white/10 bg-black/20 p-3 shadow-lg">
                        <AlbumSearchSkeleton
                            label={showSkeleton ? (searching ? (searchStage || 'Querying MusicBrainz...') : (currentProgress?.label || 'Loading album details...')) : 'Album found'}
                            album={currentAlbum}
                            loading={showSkeleton}
                            showBack={currentResultIndex > 0}
                            showNext={currentResultIndex < searchResults.length - 1}
                            onBack={goToPreviousSearchResult}
                            onNext={goToNextSearchResult}
                        />
                    </div>
                )}

                {searchError && (
                    <div className="mt-4 w-full rounded-xl border-l-4 border-red-500 bg-red-950/70 p-4 sm:max-w-lg">
                        <p className="text-red-200">Album not found. Please try a different search.</p>
                        <button
                            onClick={() => setSearchError(false)}
                            className="text-2xl text-red-400 hover:text-red-200"
                        >
                            x
                        </button>
                    </div>
                )}

                {currentAlbum && !showSkeleton && (
                    <div key={currentAlbum.mbid} className="mt-5">
                        <div className="flex flex-wrap items-center gap-3">
                            <select
                                value={mediaType}
                                onChange={(e) => setMediaType(e.target.value)}
                                className="rounded-lg border border-white/10 bg-gray-800 px-3 py-2 text-gray-200 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            >
                                <option value="CD">CD</option>
                                <option value="Tape">Tape</option>
                                <option value="Vinyl">Vinyl</option>
                            </select>
                            <button
                                className="rounded-lg bg-emerald-500 px-4 py-2 font-semibold text-white transition-colors duration-200 hover:bg-emerald-400"
                                onClick={addToCollection}
                            >
                                Add to Collection
                            </button>
                            <button
                                className="rounded-lg bg-gray-700 px-4 py-2 font-semibold text-white transition-colors duration-200 hover:bg-gray-600"
                                onClick={cancelSearch}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                )}
            </section>

            <section className="rounded-3xl border border-white/10 bg-slate-900/60 px-2 py-2 shadow-2xl backdrop-blur-sm sm:px-4 sm:py-3">
                <div className="mb-2 flex items-center justify-between px-3 py-3 sm:px-4">
                    <h2 className="text-xl font-bold tracking-wide text-white">Your Collection</h2>
                    <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs uppercase tracking-wider text-gray-300">
                        {collection.length} {collection.length === 1 ? 'entry' : 'entries'}
                    </span>
                </div>

                {collection.length === 0 && (
                    <div className="mx-2 mb-3 rounded-2xl border border-dashed border-white/15 bg-black/20 px-6 py-10 text-center">
                        <p className="text-lg text-gray-200">Your shelves are empty.</p>
                        <p className="mt-1 text-sm text-gray-400">Search for an album above to add your first record.</p>
                    </div>
                )}

                {collection.map((entry) => (
                    <div
                        key={entry.collectionEntryId}
                        className="mb-2 rounded-2xl border border-white/5 bg-gradient-to-r from-slate-900/80 to-gray-900/70 transition-colors duration-200 hover:border-white/15"
                    >
                        <div className="flex items-center border-t border-transparent px-4 py-4">
                            <div className="ml-3 flex-grow flex items-center">
                                {diarySettings.showCoverArt && (
                                    <img
                                        src={entry.albumCover || 'https://placehold.co/75x75'}
                                        alt={entry.title}
                                        className="mr-4 inline-block h-12 w-12 rounded-lg object-cover shadow-md"
                                    />
                                )}
                                <div className="flex flex-col">
                                    <div className="flex items-center">
                                        <span className="font-bold text-lg">{entry.title}</span>
                                        <p className="text-gray-400 text-sm ml-1.5 release-date">{entry.releaseDate}</p>
                                    </div>
                                    <p className="text-gray-400 text-sm">{entry.artist}</p>
                                    <p className="text-gray-400 text-sm"><strong>Media
                                        Type:</strong> {entry.mediaType}</p>
                                </div>
                            </div>
                            <div className="mr-2 flex items-center justify-center gap-2">
                                <button
                                    aria-label="Edit collection entry"
                                    className={`rounded-lg p-2 transition-colors duration-200 ${editing === entry.collectionEntryId ? 'bg-white/15 text-gray-100' : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-gray-200'}`}
                                    onClick={() => toggleEditing(entry)}
                                >
                                    <i className="fas fa-pencil-alt"></i>
                                </button>
                                <button
                                    aria-label="Show album details"
                                    className={`rounded-lg p-2 transition-colors duration-200 ${expandedDetails === entry.collectionEntryId ? 'bg-white/15 text-gray-100' : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-gray-200'}`}
                                    onClick={() => toggleDetails(entry.collectionEntryId)}
                                >
                                    <i className="fas fa-book"></i>
                                </button>
                            </div>
                        </div>
                        <div
                            className={`overflow-hidden transition-all duration-700 ease-in-out ${
                                expandedDetails === entry.collectionEntryId ? 'max-h-[520px]' : 'max-h-0'
                            }`}
                        >
                            {expandedDetails === entry.collectionEntryId && (
                                <div className="flex flex-col items-start gap-6 px-6 pb-6 pt-1 md:flex-row md:gap-12">
                                    <div className="text-gray-300 space-y-1 md:ml-10">
                                        <div className="grid w-full grid-cols-1 gap-2 md:w-[30rem] md:grid-cols-2">
                                            <p><strong>Artist:</strong> {entry.artist}</p>
                                            <p><strong>Title:</strong> {entry.title}</p>
                                            <p><strong>Country:</strong> {entry.country}</p>
                                            <p><strong>Tags:</strong></p>
                                            <p className="md:ml-10 md:w-48">{entry.tags.join(', ')}</p>
                                            <p><strong>Media Type:</strong> {entry.mediaType}</p>
                                        </div>
                                        <p><strong>MBID:</strong> {entry.MBID}</p>
                                    </div>
                                    {diarySettings.showCoverArt && (
                                        <img
                                            src={entry.albumCover || 'https://placehold.co/75x75'}
                                            alt={entry.title}
                                            className="inline-block w-full rounded-xl object-cover shadow-xl md:w-[320px]"
                                        />
                                    )}
                                </div>
                            )}
                        </div>
                        <div
                            className={`overflow-hidden transition-all duration-700 ease-in-out ${
                                editing === entry.collectionEntryId ? 'max-h-56' : 'max-h-0'
                            }`}
                        >
                            {editing === entry.collectionEntryId && (
                                <div className="flex items-start px-4 pb-5 md:px-10">
                                    <div className="flex w-full max-w-2xl flex-col gap-4 rounded-xl border border-white/10 bg-black/20 p-4 shadow-lg">
                                        <div className="flex items-start gap-2">
                                            <label
                                                htmlFor={`media-type-${entry.collectionEntryId}`}
                                                className="w-24 text-white"
                                            >
                                                Media Type:
                                            </label>
                                            <select
                                                id={`media-type-${entry.collectionEntryId}`}
                                                value={entry.mediaType}
                                                onChange={(e) => handleMediaTypeChange(entry.collectionEntryId, e.target.value)}
                                                className="flex-1 rounded-md bg-gray-800 px-2 py-1 text-gray-300 focus:ring focus:ring-emerald-300"
                                            >
                                                <option value="CD">CD</option>
                                                <option value="Tape">Tape</option>
                                                <option value="Vinyl">Vinyl</option>
                                            </select>
                                            <button
                                                onClick={() => handleDelete(entry.collectionEntryId)}
                                                className="ml-2 rounded p-2 text-gray-500 transition-colors hover:bg-red-100 hover:text-red-600"
                                            >
                                                <i className="fas fa-trash-alt"/>
                                            </button>
                                        </div>
                                        <button
                                            onClick={() => handleSaveChanges(entry.collectionEntryId)}
                                            disabled={savingEntryId === entry.collectionEntryId}
                                            className="self-start px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-60"
                                        >
                                            {savingEntryId === entry.collectionEntryId ? 'Saving...' : 'Save changes'}
                                        </button>
                                        {message && (
                                            <p className={`mt-4 text-sm ${isError ? 'text-red-400' : 'text-green-400'}`}>{message}</p>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </section>
            </div>
        </div>
    )
}

export default Collection;